import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import postal from 'postal';
import Tooltip from '@material-ui/core/Tooltip';
import { withStyles } from '@material-ui/core/styles';
import StyledTooltip from './tools-components/styled-tooltip';
import { recordStatisticsEvent } from '../../services/statistics-service';
import { bookmark, unbookmark } from '../../services/wire/bookmark-service';
import dt from '../../shared/theme';
import Hint from './bookmark-components/hint';

const Container = styled.div`
  display: flex;
  height: 40px;
  min-width: 40px;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  border: 1px solid transparent;
  padding: 0 12px;

  &.not-on-roi-page {
    ${props => dt(props).theme.isMobileOrTablet && `
      border: 1px solid #D7DFE7;
    `}

    &:hover {
      ${props => !dt(props).theme.isMobileOrTablet && `
        border: 1px solid #D7DFE7;
      `}
    }

    &:focus {
      ${props => !dt(props).theme.isMobileOrTablet && `
        border: 1px solid #D7DFE7;
      `}
    }
  }

  &:hover {
    cursor: pointer;

    ${props => dt(props).theme.isLarge && `
      border: 1px solid #D7DFE7;
    `}
  }

  .icon-lw-bookmark, .icon-lw-bookmarked {
    color: ${props => dt(props).theme.bodyColor};
    font-size: ${props => (dt(props).theme.isMobileOrTablet ? 17 : 18)}px;
  }
`;

const BookmarkedSuccessTooltip = withStyles(() => ({
  tooltip: {
    color: 'white',
    fontFamily: 'Texta, Arial, Helvetica, sans-serif',
    fontWeight: 600,
    marginTop: 4,
    backgroundColor: 'rgba(25, 32, 45, 0.85)',
    fontSize: 16,
  },
}))(Tooltip);

const BookmarkedTooltipInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  .icon-lw-bookmarked {
    color: ${props => dt(props).theme.primaryColor};
    font-size: 15px;
    margin-right: 8px;
  }
`;

const bookmarkedTooltipTitle = (
  <BookmarkedTooltipInner>
    <span className="icon-lw icon-lw-bookmarked" />
    Wire saved
  </BookmarkedTooltipInner>
);

const Label = styled.span`
  font-size: 16px;
  font-family: ${props => dt(props).theme.fontFamilyBody};
  font-weight: ${props => dt(props).theme.fontWeightHeavy};
  line-height: 120%;
  text-transform: uppercase;
  margin-left: 8px;
`;

const CHANNEL = 'wire.bookmark';
const TOPIC = 'changed';

export default function Bookmark({
  wire,
  isOnROIPage,
  showBookmarkHint,
  showLabel,
  position,
}) {
  const [isBookmarked, setIsBookmarked] = React.useState(wire.bookmarked);
  const [justBookmarked, setJustBookmarked] = React.useState(false);
  const ref = React.useRef();
  const className = `icon-lw ${isBookmarked ? 'icon-lw-bookmarked' : 'icon-lw-bookmark'}`;
  const label = isBookmarked ? 'Saved' : 'Save';
  const TooltipComponent = justBookmarked ? BookmarkedSuccessTooltip : StyledTooltip;
  // eslint-disable-next-line no-nested-ternary
  const tooltipTitle = justBookmarked ? bookmarkedTooltipTitle : isBookmarked ? 'Remove from saved wires' : 'Add to saved wires';
  const leaveDelay = justBookmarked ? 1500 : 0;

  React.useEffect(() => {
    postal.subscribe({
      channel: CHANNEL,
      topic: TOPIC,
      callback: status => setIsBookmarked(status),
    });
  }, []);

  const toggleBookmark = e => {
    e && e.preventDefault();

    if (!window.Livewire.currentUser) {
      window.location = '/sign_up';
      return;
    }

    const serviceToCall = isBookmarked ? unbookmark : bookmark;

    serviceToCall(wire.id).then(() => {
      const newBookmarkStatus = !isBookmarked;

      if (newBookmarkStatus) {
        setJustBookmarked(true);
        setTimeout(() => setJustBookmarked(false), 1500);
      }

      setIsBookmarked(newBookmarkStatus);

      const statEventType = newBookmarkStatus ? 'bookmark_created' : 'bookmark_deleted';

      recordStatisticsEvent({
        event: statEventType,
        statisticable: 'wire',
        id: wire.id,
        clicked_url: window.location.pathname,
        page: window.location.pathname,
        custom_data: {
          section: 'all_wires',
          page: 'series',
          position,
        },
      });

      if (!isOnROIPage) {
        postal.publish({
          channel: CHANNEL,
          topic: TOPIC,
          data: newBookmarkStatus,
        });
      }
    });
  };

  return (
    <>
      <TooltipComponent
        disableTouchListener
        title={tooltipTitle}
        enterDelay={300}
        leaveDelay={leaveDelay}
        {...(justBookmarked ? { open: true } : null)}
      >
        <Container ref={ref} className={`bookmark ${isOnROIPage ? '' : 'not-on-roi-page'}`} onClick={toggleBookmark}>
          <span className={className} />
          {showLabel && <Label className="label">{label}</Label>}
        </Container>
      </TooltipComponent>
      {showBookmarkHint && <Hint ref={ref} />}
    </>
  );
}

Bookmark.propTypes = {
  wire: PropTypes.shape({
    id: PropTypes.number.isRequired,
    bookmarked: PropTypes.bool.isRequired,
  }).isRequired,
  bookmarked: PropTypes.bool.isRequired,
  showBookmarkHint: PropTypes.bool,
  showLabel: PropTypes.bool,
  isOnROIPage: PropTypes.bool,
  position: PropTypes.number,
};

Bookmark.defaultProps = {
  showBookmarkHint: false,
  showLabel: false,
  isOnROIPage: false,
  position: null,
};
